/**
 * Admin — Facility & Activity definitions.
 *
 * The controlled vocabulary behind venue_facilities and venue_activities.
 * Phase 1 found five competing spellings of the same concept scattered across
 * the apps; this is the one place the vocabulary is edited so it stops drifting.
 *
 * A facility is something a venue HAS (a pool table, a stage).
 * An activity is something a venue SCHEDULES (a quiz on Tuesdays).
 *
 * Definitions are never deleted — they are deactivated, because venue rows
 * reference them (venue_facilities.facility_definition_id is ON DELETE RESTRICT).
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Redirect } from "wouter";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, EyeOff, Eye, Search, X } from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

interface Definition {
  id: number;
  name: string;
  slug: string;
  icon: string | null;
  description: string | null;
  category: string;
  sortOrder: number;
  isActive: boolean;
}

type Kind = "facility" | "activity";

const BLANK = { name: "", slug: "", icon: "", description: "", category: "", sortOrder: 0, isActive: true };
type DefForm = typeof BLANK;

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function EditPanel({ kind, editing, form, set, onSave, onCancel, saving, categories }: {
  kind: Kind;
  editing: Definition | null;
  form: DefForm;
  set: (k: keyof DefForm, v: unknown) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  categories: string[];
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 pt-10 pb-8 px-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-800">
            {editing ? `Edit ${kind} definition` : `New ${kind} definition`}
          </h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Name *</label>
              <Input
                value={form.name}
                onChange={(e) => {
                  set("name", e.target.value);
                  if (!editing) set("slug", slugify(e.target.value));
                }}
                className="h-8 text-sm"
                placeholder={kind === "facility" ? "e.g. Pool Table" : "e.g. Quiz Night"}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                Slug {editing ? <span className="font-normal text-slate-400">(immutable)</span> : "*"}
              </label>
              <Input
                value={form.slug}
                onChange={(e) => set("slug", slugify(e.target.value))}
                disabled={!!editing}
                className="h-8 text-sm font-mono disabled:bg-slate-50 disabled:text-slate-400"
                placeholder="pool-table"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Category</label>
              <Input
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                className="h-8 text-sm"
                list="def-categories"
                placeholder={kind === "facility" ? "games / access / outdoor…" : "entertainment / sport…"}
              />
              <datalist id="def-categories">
                {categories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Icon (lucide name)</label>
              <Input value={form.icon} onChange={(e) => set("icon", e.target.value)} className="h-8 text-sm font-mono" placeholder="circle" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Sort order</label>
              <Input
                type="number"
                value={String(form.sortOrder)}
                onChange={(e) => set("sortOrder", Number(e.target.value) || 0)}
                className="h-8 text-sm"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={form.isActive} onChange={(e) => set("isActive", e.target.checked)} className="rounded" />
                Active
              </label>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-500 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                className="w-full text-sm border border-input rounded-md px-3 py-2 min-h-[60px] resize-y bg-background"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800">Cancel</button>
          <button
            onClick={onSave}
            disabled={saving || !form.name.trim() || !form.slug.trim()}
            className="px-5 py-2 text-sm font-bold text-white bg-primary rounded-xl hover:bg-primary/90 disabled:opacity-40"
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDefinitions() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [kind, setKind] = useState<Kind>("facility");
  const [items, setItems] = useState<Record<Kind, Definition[]>>({ facility: [], activity: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState<Definition | null>(null);
  const [form, setForm] = useState<DefForm>(BLANK);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fRes, aRes] = await Promise.all([
        fetch("/api/admin/facility-definitions", { headers: authHeaders() }),
        fetch("/api/admin/activity-definitions", { headers: authHeaders() }),
      ]);
      const f = await fRes.json() as { items?: Definition[] };
      const a = await aRes.json() as { items?: Definition[] };
      setItems({ facility: f.items ?? [], activity: a.items ?? [] });
    } catch {
      toast({ title: "Failed to load definitions", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const current = items[kind];

  const categories = useMemo(
    () => [...new Set(current.map((d) => d.category).filter(Boolean))].sort(),
    [current],
  );

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return current
      .filter((d) => showInactive || d.isActive)
      .filter((d) => !s || d.name.toLowerCase().includes(s) || d.slug.includes(s) || d.category.toLowerCase().includes(s))
      .sort((a, b) => a.category.localeCompare(b.category) || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }, [current, search, showInactive]);

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const set = (k: keyof DefForm, v: unknown) => setForm((p) => ({ ...p, [k]: v }));
  const base = kind === "facility" ? "/api/admin/facility-definitions" : "/api/admin/activity-definitions";

  const openCreate = () => { setEditing(null); setForm({ ...BLANK, category: categories[0] ?? "" }); setPanelOpen(true); };
  const openEdit = (d: Definition) => {
    setEditing(d);
    setForm({
      name: d.name, slug: d.slug, icon: d.icon ?? "", description: d.description ?? "",
      category: d.category, sortOrder: d.sortOrder, isActive: d.isActive,
    });
    setPanelOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        icon: form.icon.trim() || undefined,
        description: form.description.trim(),
        category: form.category.trim() || "general",
        sortOrder: form.sortOrder,
        isActive: form.isActive,
      };
      if (!editing) body["slug"] = form.slug.trim();

      const res = await fetch(editing ? `${base}/${editing.id}` : base, {
        method: editing ? "PUT" : "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(d.error ?? "Save failed");
      }
      toast({ title: editing ? "Definition updated" : "Definition created" });
      setPanelOpen(false);
      await load();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Save failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (d: Definition) => {
    if (d.isActive && !window.confirm(`Deactivate "${d.name}"?\n\nVenues that already reference it keep their rows — it just stops being offered.`)) return;
    try {
      const res = await fetch(`${base}/${d.id}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ isActive: !d.isActive }),
      });
      if (!res.ok) throw new Error();
      toast({ title: d.isActive ? `"${d.name}" deactivated` : `"${d.name}" reactivated` });
      await load();
    } catch {
      toast({ title: "Failed to update definition", variant: "destructive" });
    }
  };

  return (
    <AdminLayout current="/admin/definitions">
      {panelOpen && (
        <EditPanel
          kind={kind}
          editing={editing}
          form={form}
          set={set}
          onSave={() => void handleSave()}
          onCancel={() => setPanelOpen(false)}
          saving={saving}
          categories={categories}
        />
      )}

      <div className="space-y-6">
        <div className="flex items-start gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-primary">Facilities &amp; Activities</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              The controlled vocabulary behind venue facilities and venue activities. A <strong>facility</strong> is
              something a venue <em>has</em>; an <strong>activity</strong> is something it <em>schedules</em>.
              Definitions are deactivated, never deleted — venue rows point at them.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="ml-auto flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white bg-primary rounded-xl hover:bg-primary/90"
          >
            <Plus size={14} /> New {kind}
          </button>
        </div>

        <div className="flex gap-2 flex-wrap">
          {(["facility", "activity"] as Kind[]).map((k) => (
            <button
              key={k}
              onClick={() => { setKind(k); setSearch(""); }}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all capitalize ${
                kind === k ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"
              }`}
            >
              {k} definitions
              <span className={`ml-2 text-xs rounded-full px-1.5 py-0.5 ${kind === k ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"}`}>
                {items[k].length}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative max-w-sm flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, slug or category…"
              className="w-full pl-8 pr-4 py-2 text-sm border border-slate-200 rounded-xl outline-none focus:border-primary/40 bg-white"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="rounded" />
            Show inactive
          </label>
        </div>

        {loading ? (
          <div className="text-slate-400 text-sm py-8 text-center">Loading definitions…</div>
        ) : filtered.length === 0 ? (
          <div className="text-slate-400 text-sm py-8 text-center">No definitions match.</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs">Slug</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs">Category</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs">Sort</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs">Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-600 text-xs">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((d) => (
                  <tr key={d.id} className={`hover:bg-slate-50 ${d.isActive ? "" : "opacity-60"}`}>
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-slate-800">{d.name}</span>
                      {d.description && <p className="text-[11px] text-slate-400 line-clamp-1">{d.description}</p>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{d.slug}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{d.category}</td>
                    <td className="px-4 py-2.5 text-slate-400 text-xs">{d.sortOrder}</td>
                    <td className="px-4 py-2.5">
                      {d.isActive ? (
                        <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Active</span>
                      ) : (
                        <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5 justify-end">
                        <button
                          onClick={() => openEdit(d)}
                          className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg border border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary"
                        >
                          <Pencil size={11} /> Edit
                        </button>
                        <button
                          onClick={() => void toggleActive(d)}
                          className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg border border-slate-200 text-slate-500 hover:border-slate-300"
                        >
                          {d.isActive ? <><EyeOff size={11} /> Deactivate</> : <><Eye size={11} /> Reactivate</>}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {kind === "facility" && (
          <p className="text-[11px] text-slate-400">
            Note: <code className="font-mono">bar_listings.amenities</code> is a legacy free-text array kept for
            backward compatibility. New facility data belongs in <code className="font-mono">venue_facilities</code>,
            edited per venue on the Venues screen.
          </p>
        )}
        <div className="pt-2">
          <Button variant="outline" onClick={() => void load()} className="text-xs h-8">Refresh</Button>
        </div>
      </div>
    </AdminLayout>
  );
}
